<script setup lang="ts">
import { computed } from 'vue'
import { format } from 'date-fns'
import CopyButton from '@/components/CopyButton.vue'
import GbButton from '@/components/GbButton.vue'
import { useCopyButtonPermission } from '@/components/use_copy_button_permission'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'

const props = defineProps<{
  contentType: string
  ownerName?: string
  lastUpdated?: string
  shareLink?: string
  /** The content type reported when the share link is copied. */
  shareContentType?: string
  /** Widths for the inner container, so the banner lines up with the page content beneath it. */
  containerClass?: string
  /** Closes the Copy Link confirmation and leaves a check instead (WCAG 1.4.13, 2.2.1); off by default so live pages keep theirs. */
  autoHideCopyTooltip?: boolean
}>()

const analytics = getAnalyticsTracker()
const { isCopyBlocked, reportBlockedCopy } = useCopyButtonPermission()

const formattedLastUpdated = computed(() => {
  if (!props.lastUpdated) return ''
  return format(new Date(props.lastUpdated), 'M/d/yy h:mm a')
})

function recordShareLinkCopied() {
  analytics.sendEvent(EVENT_NAMES.MISC.COPIED_SHARE_LINK, { share_content_type: props.shareContentType })
}
</script>

<template>
  <div class="bg-green-100 py-4">
    <div class="container" :class="containerClass">
      <div class="flex justify-between items-center">
        <div class="flex items-center gap-1.5">
          <span class="material-icons-round text-display-sm" aria-hidden="true">visibility</span>
          <span class="text-lg text-gray-900">
            You are viewing {{ contentType }}
            <template v-if="ownerName">
              shared by <span class="font-bold">{{ ownerName }}</span>
            </template>
          </span>
        </div>
        <div v-if="formattedLastUpdated && !shareLink" class="text-sm text-gray-900">
          <span class="font-bold">Last updated:</span> {{ formattedLastUpdated }}
        </div>
      </div>
      <div v-if="shareLink" class="mt-2.5 flex flex-col gap-x-3 gap-y-1 sm:flex-row sm:items-center sm:justify-between" data-testid="share-link">
        <div class="flex min-w-0 items-center sm:flex-1">
          <!-- A viewer who may not copy still sees the button, as on the Vue 2 page, shown disabled; it copies nothing and records no copied link.
               The preset makes aria-disabled buttons ignore the pointer, so pointer events are restored for the click to report the blocked copy as Vue 2 did. -->
          <GbButton
            v-if="isCopyBlocked"
            type="button"
            aria-disabled="true"
            gb-theme="bluePrimary"
            size="small"
            class="shrink-0 !h-10 !rounded-r-none !pointer-events-auto"
            @click="reportBlockedCopy"
          >
            <span class="material-icons-round mr-1 text-sm" aria-hidden="true">link</span> Copy Link
          </GbButton>
          <CopyButton
            v-else
            :string-to-copy="shareLink"
            button-text="Copy Link"
            tooltip-text="Link copied"
            :auto-hide-tooltip="autoHideCopyTooltip"
            :show-copied-check="autoHideCopyTooltip"
            size="small"
            custom-class="shrink-0 !h-10 !rounded-r-none"
            @copied="recordShareLinkCopied"
          >
            <template #icon>
              <span class="material-icons-round mr-1 text-sm" aria-hidden="true">link</span>
            </template>
          </CopyButton>
          <input
            :value="shareLink"
            aria-label="Share Link"
            type="text"
            readonly
            class="h-10 w-full max-w-[500px] rounded-r-xs border border-gray-600 bg-white px-3 text-text-md text-gray-700"
          >
        </div>
        <div v-if="formattedLastUpdated" class="shrink-0 text-sm text-gray-900">
          <span class="font-bold">Last updated:</span> {{ formattedLastUpdated }}
        </div>
      </div>
    </div>
  </div>
</template>
